import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { PdpEffectiveProductVm } from '../viewModels/pdp.vm';
import { formatViewContentPrice } from './pdpViewContent';
import { parseSizeList } from '@/features/pdp/utils/pdpCustomization';

function omitEmpty(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );
}

function isPlatinumProduct(metal: string, metalType: string, goldPurity: string): boolean {
  const haystack = `${metal} ${metalType} ${goldPurity}`.toLowerCase();
  return haystack.includes('platinum') || haystack.includes('950');
}

function formatPurity(
  goldPurity: string | null | undefined,
  metal: string,
  metalType: string,
): string {
  const purity = goldPurity?.trim() ?? '';
  if (isPlatinumProduct(metal, metalType, purity)) {
    if (!purity) return 'Platinum 95';
    return purity.replace(/kt$/i, '').trim();
  }
  if (!purity) return '';
  if (/kt$/i.test(purity)) return purity;
  return `${purity}KT`;
}

function isPresetSolitaireProduct(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
): boolean {
  return vm.presetSolitairePrice != null || product.presetSolitairePrice != null;
}

function buildPresetSolitaireAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
): Record<string, unknown> {
  const solitaireCarat = vm.solitaireCarat ?? product.solitaireCarat;

  return omitEmpty({
    solitaire_type: 'preset',
    solitaire_id: product.solitaireId,
    solitaire_shape: product.solitaireShape,
    solitaire_carat: solitaireCarat != null ? String(solitaireCarat) : null,
    solitaire_colour: product.solitaireColor,
    solitaire_clarity: product.solitaireClarity,
    solitaire_cut_grade: product.solitaireCutGrade,
  });
}

function resolveDefaultSizeDisplay(product: ProductDetail): string {
  const sizeKey = product.size;
  if (!sizeKey) return '';

  const sizes = parseSizeList(product.customizationOptions);
  const match = sizes.find(
    (option) =>
      String(option.key) === String(sizeKey) ||
      Number(option.key) === Number(sizeKey),
  );
  return match?.value ?? sizeKey;
}

/** PDP attributes that Meta Pixel reads from the shared trackEvent payload. */
export function buildFacebookPdpAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
  selectedSizeDisplay?: string,
): Record<string, unknown> {
  const solitaireCarat = vm.solitaireCarat ?? product.solitaireCarat;
  const diamondColorClarity = product.solitaireClarity ?? vm.diamondStoneType ?? '';

  const attributes: Record<string, unknown> = {
    content_ids: [String(product.designId)],
    content_type: 'product',
    content_names: product.designName ? [product.designName] : [],
    content_category: product.category ?? '',
    contents: [{ id: product.designId, quantity: 1 }],
    value: formatViewContentPrice(vm.discountedPrice),
    currency: 'INR',
    design_id: String(product.designId),
    variant_id: String(vm.activeCustomizationId),
    collection_name: product.collectionName ?? '',
    metal_type: vm.metalType ?? '',
    metal_colour: vm.metal ?? '',
    purity: formatPurity(vm.goldPurity, vm.metal, vm.metalType),
    stone_type: solitaireCarat ? 'Solitaire' : 'Diamond',
    diamond_quality: diamondColorClarity,
    size: selectedSizeDisplay?.trim()
      ? selectedSizeDisplay
      : resolveDefaultSizeDisplay(product),
  };

  if (isPresetSolitaireProduct(product, vm)) {
    Object.assign(attributes, buildPresetSolitaireAttributes(product, vm));
  }

  return attributes;
}
